import { A, Cursor, Marquee, Rip, Show, Track, type Css, type Key } from './engine';
import { Room, THUMBS } from './room';
import { BoxRing, C, Dot, LinkDot, SelRing, makeBar } from './ui';

/** Seleccionar: click a point, then drag it. */
export function SelectScene() {
  return (
    <>
      <Room />
      <Dot x={72} y={38} />
      <Track
        way={[
          [0, 0, 0, 2.6],
          [3.8, 50, 22, 3],
        ]}
      >
        <Dot x={124} y={44} />
        <Show a={1.35}>
          <SelRing x={124} y={44} />
        </Show>
      </Track>
      <Rip t={1.3} x={124} y={44} />
      <Cursor
        way={[
          [0, 206, 120, 0.2],
          [1.3, 124, 44, 1.3],
          [3.8, 174, 66, 1.2],
          [5.6, 214, 120, 0.2],
        ]}
        kinds={[
          [0, 'arrow'],
          [1.5, 'move'],
          [4.0, 'arrow'],
        ]}
      />
    </>
  );
}

/** Mano: the hand drags the view; the points travel with the photo. */
export function PanScene() {
  return (
    <>
      <Track
        way={[
          [0, 0, 0, 1.4],
          [3.2, 44, 0, 2.0],
          [5.9, 44, 0, 0],
          [6.5, 0, 0, 0],
        ]}
        rest={[44, 0]}
      >
        <Room ox={-256} />
        <Room />
        <Dot x={124} y={44} />
        <Dot x={62} y={86} />
        <Dot x={190} y={112} />
      </Track>
      <Cursor
        way={[
          [0, 168, 84, 1.4],
          [3.2, 212, 84, 2.0],
        ]}
        kinds={[
          [0, 'open'],
          [1.3, 'fist'],
          [3.3, 'open'],
        ]}
      />
    </>
  );
}

/** Seleccionar con recuadro: dragging a dashed box selects the points inside it. */
export function BoxSelectScene() {
  return (
    <>
      <Room />
      <Dot x={56} y={46} />
      <Dot x={120} y={40} />
      <Dot x={188} y={84} />
      <Show a={2.95}>
        <SelRing x={56} y={46} />
        <SelRing x={120} y={40} />
      </Show>
      <Marquee x={36} y={28} w={104} h={46} a={1.05} b={2.8} gone={3.1} />
      <Cursor
        way={[
          [0, 226, 122, 0.4],
          [1.0, 36, 28, 0.1],
          [2.8, 140, 74, 0.5],
          [4.6, 214, 118, 0.2],
        ]}
        kinds={[[0, 'cross']]}
      />
    </>
  );
}

/** Punto: click, a blue point appears selected. */
export function PointScene() {
  return (
    <>
      <Room />
      <Show a={1.6}>
        <Dot x={124} y={44} />
      </Show>
      <Show a={1.75}>
        <SelRing x={124} y={44} />
      </Show>
      <Rip t={1.55} x={124} y={44} />
      <Cursor
        way={[
          [0, 60, 128, 0.2],
          [1.5, 124, 44, 0.4],
          [3.5, 172, 112, 1.2],
        ]}
        kinds={[
          [0, 'cross'],
          [1.7, 'arrow'],
        ]}
      />
    </>
  );
}

const NAMES = ['Cocina', 'Dormitorio', 'Baño', 'Balcón'] as const;

/** The destination picker (panorama-link-picker.tsx): `rounded-card` popover on the dark surface. */
export function LinkPicker() {
  return (
    <g filter="url(#ta-pop)">
      <rect x="6" y="5" width="146" height="134" rx="7" fill={C.surface} fillOpacity=".96" />
      <rect x="6.5" y="5.5" width="145" height="133" rx="6.5" fill="none" stroke={C.line} />
      <circle cx="16.5" cy="16" r="3.2" fill="none" stroke={C.ink3} strokeWidth="1" />
      <path d="M18.8 18.3 L20.8 20.3" stroke={C.ink3} strokeWidth="1" strokeLinecap="round" />
      <text x="25" y="19" fontSize="8.5" fill={C.ink3}>
        ¿A qué imagen lleva?
      </text>
      <path d="M6 24.5 H152" stroke={C.line} />
      {THUMBS.map(([id], i) => {
        const x = i % 2 === 0 ? 14 : 82;
        const y = i < 2 ? 30 : 76;
        return (
          <g key={id}>
            <rect x={x} y={y} width="62" height="31" rx="3.5" fill={`url(#${id})`} />
            <path d={`M${x} ${y + 20} Q${x + 31} ${y + 13} ${x + 62} ${y + 20}`} stroke="#000" strokeOpacity=".12" fill="none" />
            <text x={x + 1} y={y + 40} fontSize="8" fill={C.ink}>
              {NAMES[i]}
            </text>
          </g>
        );
      })}
      <rect x="13" y="29" width="64" height="33" rx="4.4" fill="none" stroke={C.brand} strokeWidth="1.6" />
      <path d="M6 117.5 H152" stroke={C.line} />
      <rect x="14" y="124" width="8.5" height="8.5" rx="2.4" fill="none" stroke={C.edge} />
      <text x="27" y="131.4" fontSize="8" fill={C.ink}>
        Crear enlace de vuelta
      </text>
    </g>
  );
}

/** Conectar: click on the door, pick the destination, the link point is born and asks about the way back. */
export function PointLinkScene() {
  const ask = makeBar(
    [[{ t: 'text', s: '¿Crear el enlace de vuelta?' }, { t: 'sep' }, { t: 'btn', s: 'Ahora no', kbd: 'Esc' }, { t: 'btn', s: 'Crear vuelta', icon: 'check', kbd: 'Enter', primary: true }]],
    'bottom',
  );
  return (
    <>
      <Room />
      <Show a={1.9} b={3.75} rise>
        <LinkPicker />
      </Show>
      <Show a={3.7}>
        <LinkDot x={179} y={70} name="Cocina" />
      </Show>
      <Show a={4.1} rise>
        {ask.el}
      </Show>
      <Rip t={1.6} x={179} y={70} />
      <Rip t={3.5} x={45} y={46} color={C.brand} />
      <Cursor
        way={[
          [0, 232, 132, 0.3],
          [1.55, 179, 70, 0.5],
          [3.4, 45, 46, 0.4],
          [4.9, 236, 24, 0.2],
        ]}
        kinds={[
          [0, 'cross'],
          [1.9, 'arrow'],
        ]}
      />
    </>
  );
}

const LABEL_X = 116;
const LABEL_Y = 46;
const LABEL_H = 18;

function LabelPill({ w }: { w: number }) {
  return (
    <g filter="url(#ta-pop)">
      <rect x={LABEL_X - w / 2} y={LABEL_Y - LABEL_H / 2} width={w} height={LABEL_H} rx="5" fill="#ffffff" />
    </g>
  );
}

const LABEL_TEXT = { y: LABEL_Y, textAnchor: 'middle', dominantBaseline: 'central', fontSize: 9, fill: '#000000' } as const;

/** Stepped keys: the value changes at each `at[k]` (hard step). */
function stepped(at: readonly number[], values: readonly Css[], from: Css): Key[] {
  const keys: Key[] = [[0, from]];
  at.forEach((t, k) => {
    keys.push([t - 0.01, k === 0 ? from : (values[k - 1] ?? from)], [t, values[k] ?? from]);
  });
  return keys;
}

/**
 * Etiqueta: click to place "Nuevo label"; double-click it to open the inline
 * text field (its text selected), type a new text, click outside to confirm.
 */
export function LabelScene() {
  const W = 29; // width of "Cocina" at 9 px
  const typed = [4.5, 4.9, 5.3, 5.7, 6.1, 6.5];
  const clip = typed.map((_, k): Css => ({ clipPath: `inset(0 ${((1 - (k + 1) / 6) * 100).toFixed(1)}% 0 0)` }));
  const shift = typed.map((_, k): Css => ({ transform: `translateX(${((W - (W * (k + 1)) / 6) / 2).toFixed(2)}px)` }));
  const caret = typed.map((_, k): Css => ({ transform: `translateX(${(LABEL_X + (W * (k + 1)) / 12 + 1).toFixed(2)}px)` }));
  return (
    <>
      <Room />
      <Show a={1.6} b={7.2} final={false}>
        <LabelPill w={68} />
      </Show>
      <Show a={1.6} b={3.5} final={false}>
        <text x={LABEL_X} {...LABEL_TEXT}>
          Nuevo label
        </text>
      </Show>
      {/* inline field: the text selected, then typed over */}
      <Show a={3.5} b={4.5} final={false}>
        <rect x={LABEL_X - 26} y={LABEL_Y - 6.5} width="52" height="13" fill="rgba(61,214,199,.45)" />
        <text x={LABEL_X} {...LABEL_TEXT}>
          Nuevo label
        </text>
      </Show>
      <Show a={4.4} b={7.0} final={false}>
        <A keys={stepped(typed, shift, { transform: 'translateX(14.5px)' })} final={{ transform: 'translateX(0px)' }}>
          <A keys={stepped(typed, clip, { clipPath: 'inset(0 100% 0 0)' })} final={{ clipPath: 'inset(0 0% 0 0)' }} extra={{ transformBox: 'fill-box' }}>
            <text x={LABEL_X} {...LABEL_TEXT}>
              Cocina
            </text>
          </A>
        </A>
        <A keys={stepped(typed, caret, { transform: `translateX(${LABEL_X + 1}px)` })} final={{ transform: `translateX(${LABEL_X + 16}px)` }}>
          <rect x="0" y={LABEL_Y - 5.5} width="0.9" height="11" fill="#000000" />
        </A>
      </Show>
      <Show a={7.1}>
        <LabelPill w={W + 14} />
        <text x={LABEL_X} {...LABEL_TEXT}>
          Cocina
        </text>
      </Show>
      <Show a={1.75} b={7.2}>
        <BoxRing x={LABEL_X - 34} y={LABEL_Y - LABEL_H / 2} w={68} h={LABEL_H} />
      </Show>
      <Show a={7.1}>
        <BoxRing x={LABEL_X - (W + 14) / 2} y={LABEL_Y - LABEL_H / 2} w={W + 14} h={LABEL_H} />
      </Show>
      <Rip t={1.55} x={LABEL_X} y={LABEL_Y} />
      <Rip t={3.2} x={LABEL_X} y={LABEL_Y} />
      <Rip t={3.42} x={LABEL_X} y={LABEL_Y} r={13} />
      <Rip t={6.9} x={196} y={112} color={C.brand} />
      <Cursor
        way={[
          [0, 214, 120, 0.2],
          [1.5, LABEL_X, LABEL_Y, 2.0],
          [4.2, 196, 112, 2.7],
        ]}
        kinds={[
          [0, 'cross'],
          [1.7, 'arrow'],
        ]}
      />
    </>
  );
}

/** A neutral, generic file chooser (no app wording). */
export function FileChooser({ pickAt, pickEnd = 5.0 }: { pickAt: number; pickEnd?: number }) {
  const X = 34;
  const Y = 16;
  const files: readonly (readonly [number, string, string, string])[] = [
    [86, 'foto1.jpg', '#cfe3f2', '#7ea67a'],
    [130, 'foto2.jpg', '#f2d9b8', '#a8845c'],
    [174, 'foto3.jpg', '#d7d9ee', '#6f8f8a'],
  ];
  return (
    <g filter="url(#ta-pop)">
      <rect x={X} y={Y} width="188" height="96" rx="5" fill="#f4f4f6" stroke="#c4c4ca" />
      <path d={`M${X} ${Y + 14} V${Y + 5} Q${X} ${Y} ${X + 5} ${Y} H${X + 183} Q${X + 188} ${Y} ${X + 188} ${Y + 5} V${Y + 14} Z`} fill="#e3e3e8" />
      <text x={X + 94} y={Y + 7.4} textAnchor="middle" dominantBaseline="central" fontSize="7" fontWeight="600" fill="#33343a">
        Abrir
      </text>
      <path d={`M${X + 176} ${Y + 4} l5 5 m0 -5 l-5 5`} stroke="#6b6c74" strokeWidth="1" strokeLinecap="round" />
      <rect x={X} y={Y + 14} width="42" height="82" fill="#ececf0" />
      {['Recientes', 'Imágenes', 'Descargas'].map((s, i) => (
        <g key={s}>
          {i === 1 && <rect x={X + 3} y={Y + 18 + i * 13} width="36" height="11" rx="3" fill="#d4dcf0" />}
          <text x={X + 7} y={Y + 24 + i * 13} dominantBaseline="central" fontSize="6.4" fill="#44454c">
            {s}
          </text>
        </g>
      ))}
      {files.map(([fx, name, sky, hill], i) => (
        <g key={name}>
          {i === 1 && (
            <Show a={pickAt} b={pickEnd} final={false}>
              <rect x={fx - 4} y={Y + 18} width="44" height="45" rx="3" fill="rgba(74,123,208,.22)" stroke="#4a7bd0" strokeWidth=".8" />
            </Show>
          )}
          <rect x={fx} y={Y + 22} width="36" height="26" rx="2" fill={sky} stroke="#b9b9c0" strokeWidth=".6" />
          <path d={`M${fx} ${Y + 44} Q${fx + 12} ${Y + 33} ${fx + 22} ${Y + 40} T${fx + 36} ${Y + 38} V${Y + 48} H${fx} Z`} fill={hill} />
          <text x={fx + 18} y={Y + 56} textAnchor="middle" dominantBaseline="central" fontSize="6.2" fill="#33343a">
            {name}
          </text>
        </g>
      ))}
      <path d={`M${X + 42} ${Y + 70} H${X + 188}`} stroke="#d4d4da" />
      <rect x={X + 104} y={Y + 74} width="38" height="15" rx="3.5" fill="#e6e6ea" stroke="#c4c4ca" strokeWidth=".7" />
      <text x={X + 123} y={Y + 81.5} textAnchor="middle" dominantBaseline="central" fontSize="6.8" fill="#33343a">
        Cancelar
      </text>
      <rect x={X + 148} y={Y + 74} width="34" height="15" rx="3.5" fill="#4a7bd0" />
      <text x={X + 165} y={Y + 81.5} textAnchor="middle" dominantBaseline="central" fontSize="6.8" fill="#ffffff" fontWeight="600">
        Abrir
      </text>
    </g>
  );
}

/** Imagen: click on the wall, choose a file in the system chooser, the picture is placed. */
export function ImageScene() {
  const top = makeBar([[{ t: 'text', s: 'Haz clic donde quieras colocar la imagen' }]], 'top');
  return (
    <>
      <Room />
      <Show a={0} b={1.8} final={false}>
        {top.el}
      </Show>
      <Show a={2.2} b={5.0} rise final={false}>
        <FileChooser pickAt={3.5} />
      </Show>
      <Show a={5.2}>
        <rect x="96" y="33" width="44" height="30" rx="2" fill="#bcdcf2" stroke="#ffffff" strokeWidth="1.6" />
        <path d="M97 62 Q108 48 120 56 T139 52 V62 H97 Z" fill="#7ea67a" />
        <circle cx="128" cy="42" r="4" fill="#fff1b8" />
        <BoxRing x={96} y={33} w={44} h={30} r={2} />
      </Show>
      <Rip t={1.8} x={118} y={48} />
      <Rip t={3.4} x={148} y={50} color={C.brand} />
      <Rip t={4.8} x={199} y={97} color={C.brand} />
      <Cursor
        way={[
          [0, 226, 126, 0.3],
          [1.7, 118, 48, 0.6],
          [3.3, 148, 50, 0.5],
          [4.7, 199, 97, 0.4],
          [6.2, 214, 124, 0.2],
        ]}
        kinds={[
          [0, 'cross'],
          [2.0, 'arrow'],
        ]}
      />
    </>
  );
}
